import { useEffect, useState, useCallback, useRef } from 'react';
import { usePreferences } from '../contexts/PreferencesContext';
import { 
  announceToScreenReader, 
  FocusManager, 
  prefersReducedMotion, 
  prefersHighContrast,
  generateId 
} from '../utils/accessibility';

/**
 * Hook for managing focus trapping in modals and overlays
 */
export const useFocusTrap = (isActive: boolean) => {
  const containerRef = useRef<HTMLElement>(null);
  const focusManagerRef = useRef<FocusManager | null>(null);

  useEffect(() => {
    if (!isActive || !containerRef.current) return;

    focusManagerRef.current = new FocusManager(containerRef.current);
    const cleanup = focusManagerRef.current.trapFocus();

    return () => {
      cleanup?.();
      focusManagerRef.current?.restoreFocus();
      focusManagerRef.current = null;
    };
  }, [isActive]);

  return containerRef;
};

/**
 * Hook for managing screen reader announcements
 */
export const useScreenReader = () => {
  const announce = useCallback((message: string, priority: 'polite' | 'assertive' = 'polite') => {
    announceToScreenReader(message, priority);
  }, []);

  const announceError = useCallback((message: string) => {
    announce(`Error: ${message}`, 'assertive');
  }, [announce]);

  const announceSuccess = useCallback((message: string) => {
    announce(`Success: ${message}`, 'polite');
  }, [announce]);

  const announceLoading = useCallback((message: string = 'Loading') => {
    announce(message, 'polite');
  }, [announce]);

  return {
    announce,
    announceError,
    announceSuccess,
    announceLoading,
  };
};

/**
 * Hook for generating unique IDs for accessibility attributes
 */
export const useId = (prefix?: string) => {
  const [id] = useState(() => generateId(prefix));
  return id;
};

/**
 * Hook for detecting user accessibility preferences
 */
export const useAccessibilityPreferences = () => {
  const [reducedMotion, setReducedMotion] = useState(prefersReducedMotion);
  const [highContrast, setHighContrast] = useState(prefersHighContrast);
  const { preferences } = usePreferences();

  useEffect(() => {
    const motionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
    const contrastQuery = window.matchMedia('(prefers-contrast: high)');

    const handleMotionChange = (e: MediaQueryListEvent) => setReducedMotion(e.matches);
    const handleContrastChange = (e: MediaQueryListEvent) => setHighContrast(e.matches);

    motionQuery.addEventListener('change', handleMotionChange);
    contrastQuery.addEventListener('change', handleContrastChange);

    return () => {
      motionQuery.removeEventListener('change', handleMotionChange);
      contrastQuery.removeEventListener('change', handleContrastChange);
    };
  }, []);

  return {
    reducedMotion,
    highContrast,
    largeText: preferences.dashboardLayout === 'spacious',
    darkMode: preferences.theme === 'dark',
  };
};

/**
 * Hook for keyboard navigation in lists and menus
 */
export const useKeyboardNavigation = (
  items: HTMLElement[],
  options: {
    loop?: boolean;
    orientation?: 'horizontal' | 'vertical';
    onSelect?: (index: number) => void;
  } = {}
) => {
  const [currentIndex, setCurrentIndex] = useState(-1);
  const { loop = true, orientation = 'vertical', onSelect } = options;

  const handleKeyDown = useCallback((event: KeyboardEvent) => {
    const { key } = event;
    
    if (!items.length) return;

    let newIndex = currentIndex;

    // Navigation keys
    if (orientation === 'vertical') {
      if (key === 'ArrowDown') {
        newIndex = currentIndex + 1;
      } else if (key === 'ArrowUp') {
        newIndex = currentIndex - 1;
      }
    } else {
      if (key === 'ArrowRight') {
        newIndex = currentIndex + 1;
      } else if (key === 'ArrowLeft') {
        newIndex = currentIndex - 1;
      }
    }

    // Home/End keys
    if (key === 'Home') {
      newIndex = 0;
    } else if (key === 'End') {
      newIndex = items.length - 1;
    }

    // Selection keys
    if ((key === 'Enter' || key === ' ') && currentIndex >= 0) {
      event.preventDefault();
      onSelect?.(currentIndex);
      return;
    }

    // Handle bounds
    if (newIndex < 0) {
      newIndex = loop ? items.length - 1 : 0;
    } else if (newIndex >= items.length) {
      newIndex = loop ? 0 : items.length - 1;
    }

    if (newIndex !== currentIndex) {
      event.preventDefault();
      setCurrentIndex(newIndex);
      items[newIndex]?.focus();
    }
  }, [currentIndex, items, loop, orientation, onSelect]);

  const resetNavigation = useCallback(() => {
    setCurrentIndex(-1);
  }, []);

  const setActiveIndex = useCallback((index: number) => {
    if (index >= 0 && index < items.length) {
      setCurrentIndex(index);
      items[index]?.focus();
    }
  }, [items]);

  return {
    currentIndex,
    handleKeyDown,
    resetNavigation,
    setActiveIndex,
  };
};

/**
 * Hook for managing live regions for dynamic content updates
 */
export const useLiveRegion = () => {
  const liveRegionRef = useRef<HTMLDivElement>(null);

  const updateLiveRegion = useCallback((message: string, priority: 'polite' | 'assertive' = 'polite') => {
    if (liveRegionRef.current) {
      liveRegionRef.current.setAttribute('aria-live', priority);
      liveRegionRef.current.textContent = message;
    }
  }, []);

  const clearLiveRegion = useCallback(() => {
    if (liveRegionRef.current) {
      liveRegionRef.current.textContent = '';
    }
  }, []);

  return {
    updateLiveRegion,
    clearLiveRegion,
    liveRegionRef,
  };
};

/**
 * Hook for managing skip links
 */
export const useSkipLinks = () => {
  const skipLinks = [
    { href: '#main-content', label: 'Skip to main content' },
    { href: '#navigation', label: 'Skip to navigation' },
    { href: '#search', label: 'Skip to search' },
  ];

  return { skipLinks };
};

/**
 * Hook for managing ARIA attributes dynamically
 */
export const useAriaAttributes = () => {
  const [attributes, setAttributes] = useState<Record<string, string>>({});

  const updateAttribute = useCallback((key: string, value: string) => {
    setAttributes(prev => ({ ...prev, [key]: value }));
  }, []);

  const removeAttribute = useCallback((key: string) => {
    setAttributes(prev => {
      const { [key]: removed, ...rest } = prev;
      return rest;
    });
  }, []);

  const clearAttributes = useCallback(() => {
    setAttributes({});
  }, []);

  return {
    attributes,
    updateAttribute,
    removeAttribute,
    clearAttributes,
  };
};